<!-- Responsibility: Handle unauthenticated entry and sign-in flow.
Scope: Auth triggers and maintenance/beta status display only. -->
<login-view class="menu-overlay">
  <div class="welcome-splash" aria-label="Sign in to AnyQuest">
    <article>
      <lobby-cover-art></lobby-cover-art>

      <div class="content">
        <header>
          <h2>ANYQUEST</h2>
          <p>the adventure machine and world weaver you dreamed about.</p>
        </header>

        <p if={ state.maintenanceMode } class="welcome-status-note">Maintenance mode is active. Admin access remains available, but regular play is temporarily paused.</p>
        <p if={ !state.maintenanceMode && state.blocked } class="welcome-status-note blocked">Access Denied: Beta Invite Only. This system is currently in a closed testing phase.</p>
        <p if={ !state.maintenanceMode && !state.registrationOpen && !state.blocked } class="welcome-status-note">Guest registration is temporarily closed. Existing admins can still sign in with Google.</p>
          
        <nav aria-label="Sign in options">
          <section data-path="primary">
            <h3>Google Account</h3>
            <button
              id="btn-google-signin"
              class="action-btn primary google-btn"
              disabled={ state.busy }
              onclick={ signInGoogle }
            >
              <svg width="18" height="18" viewBox="0 0 48 48" style="flex-shrink:0" aria-hidden="true"><title>Google Sign-In</title><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
              { state.busy ? 'CONNECTING...' : 'SIGN IN WITH GOOGLE' }
            </button>
            <p if={ state.error } id="welcome-error" class="welcome-error" aria-live="polite" tabindex="-1">{ state.error }</p>
          </section>
        </nav>
      </div>
    </article>
  </div>

  <script>
    import { sessionManager } from '../../session.js'
    import { adminStore } from '../stores/admin-store.js'
    import { authStore } from '../stores/auth-store.js'
    import { sysNotify } from '../../app-states.js'
    import LobbyCoverArt from '../components/lobby/lobby-cover-art.riot'
    
    export default {
      components: {
        LobbyCoverArt,
      },

      state: { busy: false, error: null, maintenanceMode: false, registrationOpen: true, blocked: false },

      onMounted() {
        this.unmounted = false
        this.adminUnsub = adminStore.subscribe(() => {
          if (!this.unmounted) this.syncAdminFlags()
        })
        this.authUnsub = authStore.subscribe(() => {
          if (!this.unmounted) this.syncAuthStatus()
        })
        this.syncAdminFlags()
        this.syncAuthStatus()
      },

      onUnmounted() {
        this.unmounted = true
        this.adminUnsub?.()
        this.authUnsub?.()
      },

      syncAdminFlags() {
        if (this.unmounted) return
        const flags = adminStore.flags
        this.update({
          maintenanceMode: flags.maintenanceMode === true,
          registrationOpen: flags.registrationOpen !== false,
        })
      },

      syncAuthStatus() {
        if (this.unmounted) return
        this.update({
          blocked: authStore.status === 'blocked'
        })
      },

      async signInGoogle() {
        if (this.unmounted) return
        this.update({ busy: true, error: null })
        sysNotify('Connecting...')
        try {
          await sessionManager.loginWithGoogle()
          sysNotify('Identity confirmed. System Online.')
        } catch (e) {
          if (this.unmounted) return
          const msg = e?.code === 'auth/popup-closed-by-user' ? 'Sign-in cancelled.' : 'Sign-in failed. Try again.'
          this.update({ error: msg })
          sysNotify(msg)
          setTimeout(() => { if (!this.unmounted) this.$('#welcome-error')?.focus() }, 0)
        } finally {
          if (!this.unmounted) this.update({ busy: false })
        }
      },
    }
  </script>

  <style>
    .welcome-splash {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      min-height: 100dvh;
      animation: var(--motion-panel-enter, menu-hero-rise) var(--motion-slow, 0.32s) var(--motion-ease-standard, ease) both;

      > article {
        background: color-mix(in srgb, #050a11 var(--alpha-subtle), transparent);
        border: 1px solid color-mix(in srgb, var(--border) var(--alpha-ghost), transparent);
        border-radius: var(--radius-lg);
        box-shadow: 0 2rem 3.625rem rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255,255,255,0.02) inset;
        max-width: 54rem;
        width: 100%;
        overflow: hidden;
        position: relative;
        display: flex;
        flex-direction: column;
        margin: var(--space-lg);

        > .content {
          position: relative;
          z-index: 4;
          margin-top: -4rem;
          padding: var(--space-xs) var(--space-lg);

          > header {
            text-align: center;
            margin-bottom: var(--space-lg);

            > h2 {
              color: var(--gold);
              margin: 0 0 var(--space-xs);
              font-size: var(--type-2xl, var(--space-lg));
              letter-spacing: 0.12em;
              text-shadow: 0 0.25rem 1rem rgba(0,0,0,0.9);
            }

            > p {
              color: var(--text-dim);
              font-size: var(--type-lg);
              margin: 0;
              text-shadow: 0 0.125rem 0.5rem rgba(0,0,0,0.9);
            }
          }

          > p {
            text-align: center;
            font-size: var(--type-base);
            color: var(--text-prose);
            max-width: 46ch;
            margin: 0 auto var(--space-lg);
            line-height: 1.6;
          }

          > .welcome-status-note {
            margin-bottom: var(--space-lg);
            color: var(--gold);
            font-size: var(--type-sm);

            &.blocked {
              color: var(--danger);
              font-weight: bold;
              border: 1px solid var(--danger);
              padding: var(--space-sm);
              border-radius: var(--radius-sm);
              background: color-mix(in srgb, var(--danger) 10%, transparent);
            }
          }

          > nav {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: var(--space-lg);

            > section {
              background: color-mix(in srgb, var(--bg) 40%, transparent);
              border: 1px solid color-mix(in srgb, var(--border) 40%, transparent);
              border-radius: var(--space-sm);
              padding: var(--space-lg);
              display: flex;
              flex-direction: column;
              align-items: center;
              text-align: center;
              backdrop-filter: blur(0.625rem);
              transition: transform var(--motion-fast) ease, box-shadow var(--motion-fast) ease, border-color var(--motion-fast) ease;

              &:hover {
                transform: translateY(-0.125rem);
                box-shadow: 0 0.75rem 1.5rem rgba(0,0,0,0.3);
                border-color: color-mix(in srgb, var(--border) var(--alpha-high), transparent);
              }

              > h3 {
                color: var(--text-prose);
                margin: 0 0 var(--space-sm);
                font-size: var(--type-lg);
              }

              &[data-path="primary"] > h3 {
                color: color-mix(in srgb, var(--brand-primary) var(--alpha-high), var(--text-prose));
              }

              > p {
                color: var(--text-dim);
                font-size: var(--type-sm);
                line-height: 1.5;
                margin: 0 0 var(--space-lg);
                flex-grow: 1;
              }

              > button {
                width: auto;
                min-width: min(18rem, 100%);
                margin-bottom: var(--space-sm);
              }

              > small, > .welcome-error {
                color: var(--danger);
                font-size: var(--type-2xs);
                margin: 0;
              }

              > small {
                color: var(--text-prose);
                opacity: 0.9;
              }

              .google-btn {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: var(--space-xs);
              }
            }
          }
        }
      }
    }

    @media (max-width: 47.5rem) {
      .welcome-splash {
        > article {
          margin: 0;
          border-radius: 0;
          min-height: 100dvh;

          > .content {
            padding: 0 var(--space-lg) var(--space-lg);
            margin-top: -2rem;

            > nav {
              grid-template-columns: 1fr;
              justify-items: center;

              > section {
                width: min(100%, 28rem);
              }
            }
          }
        }
      }
    }
  </style>
</login-view>
